import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL?.endsWith("/api") ? BACKEND_URL : `${BACKEND_URL}/api`;

/**
 * NRA Tournament Results Bulletin — web view + Excel + print/PDF.
 * Layout mirrors docs/sample-reports and docs/NRA_BULLETIN_SPEC.md.
 */
const MatchBulletin = ({ matchId, matchName }) => {
  const [events, setEvents] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [bulletin, setBulletin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // When set, every event's bulletin is rendered stacked for a single print job.
  const [paper, setPaper] = useState("letter"); // "letter" | "legal" for PDF export
  const [downloading, setDownloading] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const eventKey = (e) =>
    `${e.event_scope}|${e.caliber || ""}|${e.match_type_instance || ""}|${e.match_no}`;

  const loadEvents = useCallback(async () => {
    try {
      setError(null);
      const res = await axios.get(
        `${API}/match-report/${matchId}/bulletin/events`,
        { headers: authHeaders() }
      );
      const list = res.data.events || [];
      setEvents(list);
      if (list.length > 0) {
        setSelectedKey(eventKey(list[0]));
      }
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.detail || "Failed to load bulletin events"
      );
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const selectedEvent = events.find((e) => eventKey(e) === selectedKey);

  const loadBulletin = useCallback(async () => {
    if (!selectedEvent) {
      setBulletin(null);
      return;
    }
    try {
      setError(null);
      const params = {
        event_scope: selectedEvent.event_scope,
        match_no: selectedEvent.match_no,
      };
      if (selectedEvent.caliber) params.caliber = selectedEvent.caliber;
      if (selectedEvent.match_type_instance) {
        params.match_type_instance = selectedEvent.match_type_instance;
      }
      const res = await axios.get(
        `${API}/match-report/${matchId}/bulletin`,
        { headers: authHeaders(), params }
      );
      setBulletin(res.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.detail || "Failed to load bulletin");
      setBulletin(null);
    }
  }, [matchId, selectedEvent]);

  useEffect(() => {
    loadBulletin();
  }, [loadBulletin]);

  const downloadExcel = async () => {
    if (!selectedEvent) return;
    try {
      const params = new URLSearchParams({
        event_scope: selectedEvent.event_scope,
        match_no: String(selectedEvent.match_no),
      });
      if (selectedEvent.caliber) params.set("caliber", selectedEvent.caliber);
      if (selectedEvent.match_type_instance) {
        params.set("match_type_instance", selectedEvent.match_type_instance);
      }
      const res = await axios.get(
        `${API}/match-report/${matchId}/bulletin/excel?${params}`,
        { headers: authHeaders(), responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `bulletin_${(selectedEvent.event_title || "event").replace(/\s+/g, "_")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Excel export failed");
    }
  };

  const printPdf = () => {
    window.print();
  };

  // Clean server-rendered PDF (no browser print chrome). `all` = every event in
  // one file; otherwise the currently selected event. `paper` picks Letter/Legal.
  const downloadPdf = async (all = false) => {
    if (all ? !events.length : !selectedEvent) return;
    setDownloading(true);
    // Name downloads after the match (strip only filesystem-illegal chars, keep it readable).
    const base = (matchName || "bulletin").replace(/[\\/:*?"<>|]+/g, "").trim() || "bulletin";
    try {
      let urlPath;
      let filename;
      if (all) {
        urlPath = `${API}/match-report/${matchId}/bulletin/pdf/all?paper=${paper}`;
        filename = `${base}.pdf`;
      } else {
        const params = new URLSearchParams({
          event_scope: selectedEvent.event_scope,
          match_no: String(selectedEvent.match_no),
          paper,
        });
        if (selectedEvent.caliber) params.set("caliber", selectedEvent.caliber);
        if (selectedEvent.match_type_instance) {
          params.set("match_type_instance", selectedEvent.match_type_instance);
        }
        urlPath = `${API}/match-report/${matchId}/bulletin/pdf?${params}`;
        const evt = (selectedEvent.event_title || "event").replace(/[\\/:*?"<>|]+/g, "").trim();
        filename = `${base} - ${evt}.pdf`;
      }
      const res = await axios.get(urlPath, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError("PDF export failed");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return <div className="text-center p-6 text-ink-500">Loading bulletin…</div>;
  }

  return (
    <div className="bulletin-root">
      <div className="no-print mb-4 flex flex-col md:flex-row md:items-end gap-3 justify-between">
        <div className="flex-1">
          <label className="field-label">
            Bulletin event
          </label>
          <select
            className="select md:w-2/3"
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value)}
          >
            {events.map((e) => (
              <option key={eventKey(e)} value={eventKey(e)}>
                #{e.match_no} — {e.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-ink-500 mt-1">
            Format matches NRA Tournament Results Bulletin samples (place awards,
            special categories, class × civilian / police-service).
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="field-label">Paper</label>
            <select
              className="select"
              value={paper}
              onChange={(e) => setPaper(e.target.value)}
            >
              <option value="letter">Letter (8.5×11)</option>
              <option value="legal">Legal (8.5×14)</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => downloadPdf(false)}
            disabled={downloading || !selectedEvent}
            className="btn-secondary"
            title="Clean PDF of the selected event"
          >
            Download PDF
          </button>
          <button
            type="button"
            onClick={() => downloadPdf(true)}
            disabled={downloading || events.length === 0}
            className="btn-primary"
            title="One clean PDF of every event's bulletin, one per page"
          >
            {downloading
              ? "Generating…"
              : `Download all${events.length ? ` (${events.length})` : ""}`}
          </button>
          <button
            type="button"
            onClick={downloadExcel}
            className="btn-secondary"
          >
            Excel
          </button>
          <button
            type="button"
            onClick={printPdf}
            className="btn-ghost"
            title="Quick browser print of the current event"
          >
            Print
          </button>
        </div>
      </div>

      {error && (
        <div className="no-print alert-error mb-4">{error}</div>
      )}

      {!bulletin && !error && (
        <div className="text-ink-500 p-4">Select an event to view the bulletin.</div>
      )}

      {bulletin ? <BulletinSheet bulletin={bulletin} /> : null}

      <style>{`
        /* Fixed layout + shared colgroup so every section's columns (and the
           score column in particular) line up at the same position. */
        .bulletin-table { border-collapse: collapse; width: 100%; table-layout: fixed; }
        .bulletin-table td { padding: 3px 6px; vertical-align: top; overflow-wrap: break-word; }
        /* Column widths on the cells themselves (mirroring <colgroup>) so the
           fixed layout — and the aligned score column — survives even when the
           print engine ignores <colgroup> (Safari does). */
        .bulletin-table td:nth-child(1) { width: 7%; }
        .bulletin-table td:nth-child(2) { width: 10%; }
        .bulletin-table td:nth-child(3) { width: 30%; }
        .bulletin-table td:nth-child(4) { width: 15%; }
        .bulletin-table td:nth-child(5) { width: 38%; }
        .bulletin-table tr.award-row td { background: #fff8e1; }
        .bulletin-table tr.special-award td { background: #e8f5e9; }
        .tabular-nums { font-variant-numeric: tabular-nums; }

        @media print {
          @page {
            size: letter portrait;
            margin: 0.5in;
          }
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          /* The report wrapper (.page = container mx-auto px-4 py-8) would add
             its own centered max-width + padding on top of the @page margin,
             making the printed margins look uneven. Neutralize it so the @page
             margin is the single source of truth. */
          .page {
            max-width: none !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          body * { visibility: hidden; }
          .bulletin-root, .bulletin-root * { visibility: visible; }
          /* Collapse the app chrome (nav is hidden here; the report header and
             tab bar carry .no-print) and keep the bulletin in NORMAL FLOW, not
             absolutely positioned — an absolute print root drops
             page-break-before between stacked bulletins in Safari, which left
             the next report's title stranded mid-page. */
          nav { display: none !important; }
          .bulletin-root {
            position: static;
            width: 100%;
            margin: 0;
            padding: 0;
          }
          .no-print { display: none !important; }
          .bulletin-sheet {
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            font-size: 10.5pt;
            color: #000 !important;
          }
          .bulletin-title { font-size: 14pt; letter-spacing: 0.04em; }
          .section-title {
            font-size: 11pt;
            page-break-after: avoid;
            break-after: avoid;
          }
          .bulletin-section {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .bulletin-table tr { page-break-inside: avoid; }
          .bulletin-table tr.award-row td,
          .bulletin-table tr.special-award td {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .bulletin-footer {
            font-size: 8pt;
            color: #444 !important;
            margin-top: 1.2rem;
          }
        }
      `}</style>
    </div>
  );
};

function BulletinTable({ rows, showPlace, empty, highlightAwards }) {
  if (!rows || rows.length === 0) {
    return <p className="text-ink-500 italic">{empty || "None."}</p>;
  }
  return (
    <table className="w-full bulletin-table">
      <BulletinCols />
      <tbody>
        {rows.map((r, i) => {
          const award = r.award_label || "";
          const isAward =
            highlightAwards &&
            award &&
            (/Winner|First|Second|Third|Fourth|High /.test(award));
          return (
            <tr key={i} className={isAward ? "award-row" : undefined}>
              <td className="pr-2 text-right tabular-nums">
                {showPlace ? r.place ?? "" : ""}
              </td>
              <td className="pr-2 text-right tabular-nums">
                {r.competitor_number ?? ""}
              </td>
              <td className="pr-2">{r.name_display}</td>
              <td className="pr-3 text-right tabular-nums whitespace-nowrap font-medium">
                {r.score_display}
              </td>
              <td className="text-right text-ink-800">{award}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Shared column grid for every bulletin section, so place / competitor # /
// name / score / award all line up at the same x across sections (with
// table-layout: fixed). Score gets a fixed band and is right-aligned.
function BulletinCols() {
  // Widths: place · competitor# · name · score · award. Name is kept snug so
  // the (right-aligned) score sits close after the name instead of drifting
  // far right toward the award. Tune `name` vs `award` to shift the balance.
  return (
    <colgroup>
      <col style={{ width: "7%" }} />
      <col style={{ width: "10%" }} />
      <col style={{ width: "30%" }} />
      <col style={{ width: "15%" }} />
      <col style={{ width: "38%" }} />
    </colgroup>
  );
}

// One full bulletin sheet (header + all sections). Rendered once for the
// on-screen/selected view, or once per event when printing every bulletin.
function BulletinSheet({ bulletin }) {
  return (
    <div className="bulletin-sheet bg-white shadow rounded p-6 md:p-10 font-serif text-sm text-black">
      <div className="bulletin-header text-center mb-6">
        <div className="bulletin-title text-lg font-bold tracking-wide uppercase">
          {bulletin.header.bulletin_title}
        </div>
        <div className="font-semibold mt-1">{bulletin.header.tournament_title}</div>
        <div className="mt-1">{bulletin.header.location}</div>
        <div className="font-bold mt-4 text-base">
          MATCH NO. {bulletin.header.match_no} -- {bulletin.header.event_title}
        </div>
      </div>

      <section className="bulletin-section mb-6">
        <h3 className="section-title font-bold border-b-2 border-black pb-1 mb-2">
          OPEN -- PLACE AWARDS ({bulletin.open_competitor_count ?? bulletin.competitor_count} COMPETITORS)
        </h3>
        <BulletinTable
          rows={bulletin.open_place_awards}
          showPlace
          empty="No scores for this event."
          highlightAwards
        />
      </section>

      <section className="bulletin-section mb-6">
        <h3 className="section-title font-bold border-b-2 border-black pb-1 mb-2">
          SPECIAL CATEGORY AWARDS
        </h3>
        {bulletin.special_category_awards?.length ? (
          <table className="w-full bulletin-table">
            <BulletinCols />
            <tbody>
              {bulletin.special_category_awards.map((r, i) => (
                <tr key={i} className="award-row special-award">
                  {/* empty place cell so columns align with the other sections */}
                  <td aria-hidden="true"></td>
                  <td className="pr-2 text-right tabular-nums">
                    {r.competitor_number ?? ""}
                  </td>
                  <td className="pr-2">{r.name_display}</td>
                  <td className="pr-3 text-right tabular-nums whitespace-nowrap font-medium">
                    {r.score_display}
                  </td>
                  <td className="text-right font-medium">{r.award_label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-ink-500 italic">
            No special-category winners (set division / special categories on
            shooters).
          </p>
        )}
      </section>

      {bulletin.class_sections?.map((sec) => (
        <section key={sec.title} className="bulletin-section mb-6">
          <h3 className="section-title font-bold border-b-2 border-black pb-1 mb-2">
            {sec.title} ({sec.competitor_count} COMPETITORS)
          </h3>
          <BulletinTable rows={sec.rows} showPlace highlightAwards />
        </section>
      ))}

      <div className="bulletin-footer text-center text-xs text-ink-500 mt-8 pt-4 border-t border-ink-200">
        <span className="no-print">
          Tip: Print / PDF → “Save as PDF”. Margins set for clean handouts.{" "}
        </span>
        <span>
          {bulletin.header.location}
          {bulletin.header.event_title
            ? ` · ${bulletin.header.event_title}`
            : ""}
        </span>
      </div>
    </div>
  );
}

export default MatchBulletin;
